import { forwardRef, useEffect, useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { IconButton } from '~/components/IconButton';
import { Label } from '~/components/Label/Label';
import { Separator } from '~/components/Separator';
import { env } from '~/lib/env';
import type { ReleasesFilters } from '~/modules/artist-roster/hooks/useReleasesUrlParams/types';

import { DEFAULT_RELEASES_FILTERS } from './constants';
import { TrackReleaseTypeFilter } from './TrackReleaseTypeFilter/TrackReleaseTypeFilter';
import { TrackRoleFilter } from './TrackRoleFilter/TrackRoleFilter';

interface TrackFilterPanelProps {
  originalFilters: ReleasesFilters;
  onClose?: () => void;
  onApplyFilters: (filters: ReleasesFilters) => void;
}

export const TrackFilterPanel = forwardRef<
  HTMLFormElement,
  TrackFilterPanelProps
>(({ originalFilters, onClose, onApplyFilters }, ref) => {
  const { t } = useTranslation('roster');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const methods = useForm<ReleasesFilters>({
    defaultValues: structuredClone(originalFilters),
  });
  const {
    formState: { isDirty },
    reset,
    handleSubmit,
    watch,
  } = methods;

  const isArtistCollaboratorTracksEnabled =
    env('VITE_WMG_ARTIST_COLLABORATOR_TRACKS_ENABLED') === 'true';

  const onSubmit: SubmitHandler<ReleasesFilters> = (formData) => {
    onApplyFilters(formData);
    onClose?.();
  };

  // Focus inside the Filters panel after it opens.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [closeButtonRef]);

  const handleClearFiltersButtonClick = () =>
    reset(DEFAULT_RELEASES_FILTERS, { keepDefaultValues: true });

  const isClearFiltersButtonDisabled =
    JSON.stringify(watch()) === JSON.stringify(DEFAULT_RELEASES_FILTERS);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className="flex basis-full flex-col"
      >
        <div className="flex justify-between p-lg pb-md">
          <div className="text-headline-md font-bold">
            {t('filters.heading')}
          </div>
          <IconButton
            ref={closeButtonRef}
            type="button"
            iconType="xmark"
            iconVariant="regular"
            size="large"
            onClick={onClose}
            className="-mr-sm -mt-sm duration-300 ease-out"
          />
        </div>
        <div className="grow overflow-y-scroll px-lg py-md">
          <div className="flex flex-col gap-lg">
            <section>
              <Label className="font-semibold">
                {t('filters.releaseType.heading')}
              </Label>
              <TrackReleaseTypeFilter />
            </section>
            {isArtistCollaboratorTracksEnabled ? (
              <>
                <Separator />
                <section>
                  <Label className="font-semibold">
                    {t('filters.role.heading')}
                  </Label>
                  <TrackRoleFilter />
                </section>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end gap-lg border-t-px px-lg py-md bg-level-03 border-emphasis-subtle">
          <Button
            size="large"
            variant="tertiary"
            disabled={isClearFiltersButtonDisabled}
            onClick={handleClearFiltersButtonClick}
          >
            {t('filters.clearFilters')}
          </Button>
          <Button size="large" disabled={!isDirty} type="submit">
            {t('filters.apply')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
});

TrackFilterPanel.displayName = 'TrackFilterPanel';
